import { useState, useEffect, useRef, useCallback } from 'react';
import projectXWS from '../services/projectxWebSocket';
import { getRealtimeConfig } from '../services/api.trading';

// Contract ID mapping for ProjectX
const CONTRACT_MAP = {
  "ES": "CON.F.US.EP",
  "NQ": "CON.F.US.ENQ",
  "MES": "CON.F.US.MES",
  "MNQ": "CON.F.US.MNQ",
  "CL": "CON.F.US.CLE",
  "MCL": "CON.F.US.MCLE",
  "GC": "CON.F.US.GCE",
  "MGC": "CON.F.US.MGC",
  "SI": "CON.F.US.SIE",
  "SIL": "CON.F.US.SIL",
  "RTY": "CON.F.US.RTY",
  "YM": "CON.F.US.YM",
};

export const useMarketData = ({
  symbol,
  selectedTradingAccountId,
  selectedExternalAccountId,
  onAccountUpdate, // Callback for account updates (balance, PnL)
  onBrokerStateUpdate, // Callback for order/position updates
}) => {
  const [wsConnected, setWsConnected] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [bidPrice, setBidPrice] = useState(0);
  const [askPrice, setAskPrice] = useState(0);
  const [dayHigh, setDayHigh] = useState(0);
  const [dayLow, setDayLow] = useState(0);
  const [volume, setVolume] = useState(0);
  const [priceChange, setPriceChange] = useState(0);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [positionMarksBySymbol, setPositionMarksBySymbol] = useState({});
  const [contractMap, setContractMap] = useState({});

  const currentContractRef = useRef(null);
  const quoteUnsubscribeRef = useRef(null);
  const depthUnsubscribeRef = useRef(null);
  const lastDisplayedPriceRef = useRef(0);
  const liveQuoteActiveRef = useRef(false);
  const lastQuoteReceivedAtRef = useRef(0);

  // Helper to update position marks efficiently
  const mergePositionMark = useCallback((symbol, markPrice) => {
    setPositionMarksBySymbol((prev) => ({
      ...prev,
      [symbol]: markPrice,
    }));
  }, []);

  const subscribeToSelectedContract = useCallback((nextContractId) => {
    if (!nextContractId) return;

    quoteUnsubscribeRef.current?.();
    depthUnsubscribeRef.current?.();

    // Subscribe to Quotes
    quoteUnsubscribeRef.current = projectXWS.subscribeToQuotes(nextContractId, (quote) => {
      lastQuoteReceivedAtRef.current = Date.now();
      liveQuoteActiveRef.current = true;

      const nextPrice = Number(quote.price || 0);
      if (Number.isFinite(nextPrice) && nextPrice > 0) {
        const previousPrice = lastDisplayedPriceRef.current;
        setCurrentPrice(nextPrice);
        mergePositionMark(symbol, nextPrice);
        if (Number.isFinite(previousPrice) && previousPrice > 0) {
          setPriceChange(((nextPrice - previousPrice) / previousPrice) * 100);
        }
        lastDisplayedPriceRef.current = nextPrice;
      }

      const nextBid = Number(quote.bid || 0);
      if (Number.isFinite(nextBid) && nextBid > 0) setBidPrice(nextBid);

      const nextAsk = Number(quote.ask || 0);
      if (Number.isFinite(nextAsk) && nextAsk > 0) setAskPrice(nextAsk);

      setRecentQuotes((prev) => {
        const newQuotes = [...prev, {
          time: quote.timestamp || quote.time || quote.tradeTime || new Date().toISOString(),
          price: quote.price,
          bid: quote.bid,
          ask: quote.ask,
        }];
        return newQuotes.slice(-240);
      });
    });

    // Subscribe to Market Depth (High/Low/Vol)
    depthUnsubscribeRef.current = projectXWS.subscribeToMarketDepth(nextContractId, (depth) => {
      if (depth.high) setDayHigh(depth.high);
      if (depth.low) setDayLow(depth.low);
      if (depth.volume) setVolume(depth.volume);
    });
  }, [symbol, mergePositionMark]);

  const initializeRealtimeFeed = useCallback(async () => {
    try {
      const realtimeRes = await getRealtimeConfig(selectedTradingAccountId
        ? { trading_account_id: selectedTradingAccountId }
        : undefined);

      if (!realtimeRes.data?.success) {
        setWsConnected(false);
        return;
      }

      projectXWS.setToken(localStorage.getItem('token'));
      projectXWS.setAccountId(realtimeRes.data.accountId || null);
      setContractMap(realtimeRes.data.contractMap || {});

      const connected = await projectXWS.connect();
      setWsConnected(Boolean(connected));
      if (!connected) {
        liveQuoteActiveRef.current = false;
        return;
      }

      const nextContractId = (realtimeRes.data.contractMap || {})[symbol] || CONTRACT_MAP[symbol];
      if (currentContractRef.current && currentContractRef.current !== nextContractId) {
        await projectXWS.unsubscribe(currentContractRef.current);
      }
      currentContractRef.current = nextContractId || null;
      setRecentQuotes([]);
      liveQuoteActiveRef.current = false;
      lastQuoteReceivedAtRef.current = 0;
      subscribeToSelectedContract(nextContractId);
    } catch (error) {
      console.error("WebSocket initialization error:", error);
      setWsConnected(false);
      liveQuoteActiveRef.current = false;
    }
  }, [selectedTradingAccountId, symbol, subscribeToSelectedContract]);

  // Main Effect: Initialize Feed
  useEffect(() => {
    initializeRealtimeFeed();

    return () => {
      quoteUnsubscribeRef.current?.();
      depthUnsubscribeRef.current?.();
    };
  }, [initializeRealtimeFeed]);

  // Account & Order Updates Effect
  useEffect(() => {
    if (!wsConnected) return;

    const handleAccountUpdate = (payload) => {
      if (onAccountUpdate) onAccountUpdate(payload);
    };

    const handleBrokerStateUpdate = (payload) => {
        if (onBrokerStateUpdate) onBrokerStateUpdate(payload);
    };

    const unsubscribeAccount = projectXWS.subscribe("account", handleAccountUpdate);
    const unsubscribePosition = projectXWS.subscribe("position", handleBrokerStateUpdate);
    const unsubscribeOrder = projectXWS.subscribe("order", handleBrokerStateUpdate);

    return () => {
      unsubscribeAccount?.();
      unsubscribePosition?.();
      unsubscribeOrder?.();
    };
  }, [wsConnected, onAccountUpdate, onBrokerStateUpdate]);

  return {
    wsConnected,
    currentPrice,
    bidPrice,
    askPrice,
    dayHigh,
    dayLow,
    volume,
    priceChange,
    recentQuotes,
    positionMarksBySymbol,
    contractMap,
    initializeRealtimeFeed // exposed if manual refresh needed
  };
};
